"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale, type TFn } from "@/lib/i18n";
import { Link } from "@/i18n/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import { cachedFetch } from "@/lib/fetch-cache";
import { imageUrl } from "@/lib/image-url";
import { captureIsComplete, combatCounts, hasMapPositions, parseReplay, routeForAct, type ReplayFloor, type ReplayModel } from "@/lib/replay";
import { useEntityScores } from "@/lib/use-entity-scores";
import LiveMap from "@/app/[locale]/live/LiveMap";
import { characterName, useCharacterNames, useEncounterMap, useMonsterMap, type Coord } from "@/app/[locale]/live/live-shared";
import { cleanId, type CardInfo, type PotionInfo, type RelicInfo } from "../RunPills";
import FloorPanel, { KIND_LABEL, floorTitle, type Catalog, type EventInfo } from "./FloorPanel";
import type { ReplayRunInfo } from "./page";
import { PlayerBadge } from "@/app/components/SupporterBadge";
import { OwnerTheme } from "@/app/components/OwnerTheme";
import { GameTablesProvider } from "@/app/components/GameMessagesProvider";
import { useTryGameTranslations } from "@/lib/game-i18n";
import { REPLAY_TABLES } from "@/lib/game-messages.common";
const ENERGY_ICONS = new Set(["ironclad", "silent", "defect", "watcher", "necrobinder", "regent"]);
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const KIND_GLYPH: Record<string, string> = {
  combat: "⚔",
  monster: "⚔",
  elite: "☠",
  boss: "♛",
  merchant: "$",
  shop: "$",
  restsite: "🔥",
  rest: "🔥",
  treasure: "▣",
  event: "?",
  unknown: "?",
  ancient: "◈"
};
function formatTime(sec: number | undefined): string {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
}
interface Series {
  key: string;
  label: string;
  kind: "line" | "bar";
  color: string;
  values: (number | undefined)[];
  max?: number;
  suffix?: string;
}
function seriesFor(model: ReplayModel, floors: ReplayFloor[], maxHp: number | undefined, t: TFn): Series[] {
  let deck = model.startingDeck.length;
  const deckSizes: number[] = [];
  for (const f of floors) {
    for (const l of f.lines) {
      if (l.t === "acquire") deck += 1;else if (l.t === "remove") deck -= 1;
    }
    deckSizes.push(deck);
  }
  return [{
    key: "hp",
    label: "HP",
    kind: "line",
    color: "var(--accent-red)",
    values: floors.map(f => f.hpAfter),
    max: maxHp,
    suffix: maxHp ? `/${maxHp}` : ""
  }, {
    key: "gold",
    label: t("Gold"),
    kind: "line",
    color: "var(--accent-gold)",
    values: floors.map(f => f.goldAfter)
  }, {
    key: "deck",
    label: t("Deck size"),
    kind: "line",
    color: "var(--text-secondary)",
    values: deckSizes
  }, {
    key: "dmg",
    label: t("HP lost per floor"),
    kind: "bar",
    color: "var(--accent-red)",
    // Only the recorder's own totals. The recorded HP changes are a lower
    // bound per fight, so adding them into the same series would present two
    // different measurements as one. A floor with any unknown fight is
    // unknown, not smaller. An attempt a reload threw away is left out: its
    // HP loss was rolled back with it, and every number in it is real, which
    // is what would make the double count hard to see.
    values: floors.map(f => {
      const kept = f.combats.filter(combatCounts);
      return kept.length && kept.every(c => c.hpLost !== undefined) ? kept.reduce((n, c) => n + (c.hpLost ?? 0), 0) : undefined;
    })
  }, {
    key: "turns",
    label: t("Turns per floor"),
    kind: "bar",
    color: "var(--text-secondary)",
    values: floors.map(f => {
      const kept = f.combats.filter(combatCounts);
      return kept.length ? kept.reduce((n, c) => n + (c.turnCount ?? c.turns.filter(x => x.side === "player").length), 0) : undefined;
    })
  }];
}
function Chart(t0) {
  const $ = _c(71);
  const {
    s,
    floors,
    selected,
    onPick
  } = t0;
  const t = useT();
  useGameLocale();
  const n = Math.max(1, floors.length);
  const slotW = 100 / n;
  let t1;
  if ($[0] !== slotW) {
    t1 = i => (i + 0.5) * slotW;
    $[0] = slotW;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const xs = t1;
  const t2 = s.values;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== floors || $[3] !== s.color || $[4] !== s.kind || $[5] !== s.label || $[6] !== s.max || $[7] !== s.suffix || $[8] !== s.values || $[9] !== selected || $[10] !== slotW || $[11] !== xs) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const present = t2.map(_temp).filter(_temp2);
      if (!present.length) {
        t12 = null;
        break bb0;
      }
      const top = Math.max(s.max ?? 0, ...present.map(_temp3), 1);
      let t13;
      if ($[22] !== top) {
        t13 = v_1 => 31 - v_1 / top * 29;
        $[22] = top;
        $[23] = t13;
      } else {
        t13 = $[23];
      }
      const y = t13;
      let t14;
      if ($[24] !== selected) {
        t14 = f => f.floor === selected;
        $[24] = selected;
        $[25] = t14;
      } else {
        t14 = $[25];
      }
      const sel = floors.findIndex(t14);
      const selVal = sel >= 0 ? s.values[sel] : undefined;
      const bw = Math.max(1.2, slotW * 0.6);
      if ($[26] !== floors || $[27] !== s.label || $[28] !== s.suffix || $[29] !== selVal || $[30] !== slotW) {
        let t15;
        if ($[38] !== floors) {
          t15 = (f_0, i_1) => i_1 > 0 && f_0.act !== floors[i_1 - 1].act ? i_1 : -1;
          $[38] = floors;
          $[39] = t15;
        } else {
          t15 = $[39];
        }
        const actStarts = floors.map(t15).filter(_temp4);
        t10 = "rounded-md border border-[var(--border-subtle)] bg-[var(--bg-card)] px-2 pb-0.5 pt-1";
        let t16;
        if ($[40] !== s.label) {
          t16 = <span>{s.label}</span>;
          $[40] = s.label;
          $[41] = t16;
        } else {
          t16 = $[41];
        }
        const t17 = selVal !== undefined ? `${selVal}${s.suffix ?? ""}` : "";
        let t18;
        if ($[42] !== t17) {
          t18 = <span className="tabular-nums text-[var(--text-secondary)]">{t17}</span>;
          $[42] = t17;
          $[43] = t18;
        } else {
          t18 = $[43];
        }
        if ($[44] !== t16 || $[45] !== t18) {
          t11 = <div className="flex justify-between text-[10px] text-[var(--text-muted)]">{t16}{t18}</div>;
          $[44] = t16;
          $[45] = t18;
          $[46] = t11;
        } else {
          t11 = $[46];
        }
        t3 = "0 0 100 32";
        t4 = "h-10 w-full";
        t5 = "none";
        t6 = s.label;
        let t19;
        if ($[47] !== slotW) {
          t19 = i_3 => <line key={i_3} x1={i_3 * slotW} x2={i_3 * slotW} y1={0} y2={32} stroke="var(--border-subtle)" strokeWidth={1} vectorEffect="non-scaling-stroke" />;
          $[47] = slotW;
          $[48] = t19;
        } else {
          t19 = $[48];
        }
        t7 = actStarts.map(t19);
        $[26] = floors;
        $[27] = s.label;
        $[28] = s.suffix;
        $[29] = selVal;
        $[30] = slotW;
        $[31] = t10;
        $[32] = t11;
        $[33] = t3;
        $[34] = t4;
        $[35] = t5;
        $[36] = t6;
        $[37] = t7;
      } else {
        t10 = $[31];
        t11 = $[32];
        t3 = $[33];
        t4 = $[34];
        t5 = $[35];
        t6 = $[36];
        t7 = $[37];
      }
      t8 = s.kind === "line" ? <path d={present.map((t15, k) => {
        const [v_2, i_4] = t15;
        return `${k === 0 ? "M" : "L"}${xs(i_4).toFixed(1)},${y(v_2).toFixed(1)}`;
      }).join(" ")} fill="none" stroke={s.color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" /> : present.map(t16 => {
        const [v_3, i_5] = t16;
        return <rect key={i_5} x={xs(i_5) - bw / 2} y={y(v_3)} width={bw} height={Math.max(0.5, 31 - y(v_3))} fill={s.color} opacity={i_5 === sel ? 1 : 0.7} />;
      });
      t9 = sel >= 0 && <line x1={xs(sel)} x2={xs(sel)} y1={0} y2={32} stroke="var(--accent-gold)" strokeWidth={1} vectorEffect="non-scaling-stroke" />;
    }
    $[2] = floors;
    $[3] = s.color;
    $[4] = s.kind;
    $[5] = s.label;
    $[6] = s.max;
    $[7] = s.suffix;
    $[8] = s.values;
    $[9] = selected;
    $[10] = slotW;
    $[11] = xs;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[49] !== floors || $[50] !== onPick || $[51] !== slotW || $[52] !== t) {
    let t14;
    if ($[54] !== onPick || $[55] !== slotW || $[56] !== t) {
      t14 = (f_1, i_6) => <rect key={f_1.floor} x={i_6 * slotW} y={0} width={slotW} height={32} fill="transparent" onClick={() => onPick(f_1.floor)} style={{
        cursor: "pointer"
      }}><title>{`${t("floor")} ${f_1.floor}`}</title></rect>;
      $[54] = onPick;
      $[55] = slotW;
      $[56] = t;
      $[57] = t14;
    } else {
      t14 = $[57];
    }
    t13 = floors.map(t14);
    $[49] = floors;
    $[50] = onPick;
    $[51] = slotW;
    $[52] = t;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[58] !== t13 || $[59] !== t3 || $[60] !== t4 || $[61] !== t5 || $[62] !== t6 || $[63] !== t7 || $[64] !== t8 || $[65] !== t9) {
    t14 = <svg viewBox={t3} className={t4} preserveAspectRatio={t5} aria-label={t6}>{t7}{t8}{t9}{t13}</svg>;
    $[58] = t13;
    $[59] = t3;
    $[60] = t4;
    $[61] = t5;
    $[62] = t6;
    $[63] = t7;
    $[64] = t8;
    $[65] = t9;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  let t15;
  if ($[67] !== t10 || $[68] !== t11 || $[69] !== t14) {
    t15 = <div className={t10}>{t11}{t14}</div>;
    $[67] = t10;
    $[68] = t11;
    $[69] = t14;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  return t15;
}
function _temp4(i_2) {
  return i_2 > 0;
}
function _temp3(t0) {
  const [v_0] = t0;
  return v_0;
}
function _temp2(p) {
  return p[0] !== undefined;
}
function _temp(v, i_0) {
  return [v, i_0] as const;
}
function RunCharts(t0) {
  const $ = _c(16);
  const {
    model,
    floors,
    maxHp,
    selected,
    onPick
  } = t0;
  const t = useT();
  useGameLocale();
  if (floors.length < 2) {
    return null;
  }
  let t1;
  if ($[0] !== t) {
    t1 = t("Run progress");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== floors || $[3] !== maxHp || $[4] !== model || $[5] !== onPick || $[6] !== selected || $[7] !== t) {
    let t3;
    if ($[9] !== floors || $[10] !== onPick || $[11] !== selected) {
      t3 = s => <Chart key={s.key} s={s} floors={floors} selected={selected} onPick={onPick} />;
      $[9] = floors;
      $[10] = onPick;
      $[11] = selected;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    t2 = seriesFor(model, floors, maxHp, t).map(t3);
    $[2] = floors;
    $[3] = maxHp;
    $[4] = model;
    $[5] = onPick;
    $[6] = selected;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = <section className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" aria-label={t1}>{t2}</section>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
export default function ReplayClient(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = <ReplayBody {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.run.is_beta || $[3] !== t0) {
    t1 = <GameTablesProvider tables={REPLAY_TABLES} beta={props.run.is_beta}>{t0}</GameTablesProvider>;
    $[2] = props.run.is_beta;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function ReplayBody(t0) {
  const $ = _c(109);
  const {
    hash,
    run
  } = t0;
  const t = useT();
  const lang = useGameLocale();
  const lp = useBetaPrefix();
  const gt = useTryGameTranslations();
  const [model, setModel] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cards, setCards] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [relics, setRelics] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [potions, setPotions] = useState(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [events, setEvents] = useState(t4);
  const monsters = useMonsterMap(true);
  const encounters = useEncounterMap(true);
  const bracket = (run.ascension ?? 0) >= 10 ? "a10" : "all";
  const cardScores = useEntityScores("cards", bracket);
  const relicScores = useEntityScores("relics", bracket);
  let t5;
  let t6;
  if ($[4] !== hash) {
    t5 = () => {
      let alive = true;
      fetch(`${API}/api/runs/${encodeURIComponent(hash)}/replay`).then(_temp5).then(text => {
        if (!alive) {
          return;
        }
        const parsed = parseReplay(text);
        setModel(parsed);
        const wanted = parseInt(new URLSearchParams(window.location.search).get("floor") || "", 10);
        const first = parsed.floors.find(f => f.floor === wanted) ?? parsed.floors[0];
        setSelected(first?.floor ?? null);
      }).catch(e => alive && setError(e instanceof Error ? e.message : "failed"));
      return () => {
        alive = false;
      };
    };
    t6 = [hash];
    $[4] = hash;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] !== lang) {
    t7 = () => {
      let alive_0 = true;
      const index = _temp6;
      const load = path => cachedFetch(`${API}/api/${path}?lang=${encodeURIComponent(lang)}`).then(index, _temp7);
      Promise.all([load("cards"), load("relics"), load("potions"), load("events")]).then(t9 => {
        const [c, r, p, e_0] = t9;
        if (!alive_0) {
          return;
        }
        setCards(c);
        setRelics(r);
        setPotions(p);
        setEvents(e_0);
      });
      return () => {
        alive_0 = false;
      };
    };
    t8 = [lang];
    $[7] = lang;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] !== model?.floors) {
    t9 = model?.floors ?? [];
    $[10] = model?.floors;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const floors = t9;
  let t10;
  if ($[12] !== selected) {
    t10 = f_0 => f_0.floor === selected;
    $[12] = selected;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const current = floors.find(t10);
  const act = current?.act ?? floors[0]?.act ?? 1;
  const acts = Array.from(new Set(floors.map(_temp8))).sort(_temp9);
  const route = model ? routeForAct(model, act) : [];
  const path_0 = route.flatMap(_temp10);
  const selectedCoord = current ? route.find(e_2 => e_2.floor.floor === current.floor)?.coord : undefined;
  const coordToFloor = new Map();
  for (const e_3 of route) {
    if (e_3.coord) {
      coordToFloor.set(`${e_3.coord[0]},${e_3.coord[1]}`, e_3.floor.floor);
    }
  }
  const pathEdges = new Set();
  for (let i = 1; i < route.length; i = i + 1, i) {
    const from = route[i - 1].coord;
    const to = route[i].coord;
    if (from && to) {
      pathEdges.add(`${from[0]},${from[1]}>${to[0]},${to[1]}`);
    }
  }
  const unplaced = route.filter(_temp11).length;
  const offMap = route.filter(_temp12).length;
  const positionsRecorded = model ? hasMapPositions(model) : false;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = floor => {
      setSelected(floor);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("floor", String(floor));
        window.history.replaceState(null, "", url.toString());
      } catch {}
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const pick = t11;
  let t12;
  let t13;
  if ($[15] !== floors || $[16] !== selected) {
    t12 = () => {
      const onKey = e_6 => {
        if (!floors.length || selected === null) {
          return;
        }
        if (e_6.key !== "ArrowLeft" && e_6.key !== "ArrowRight") {
          return;
        }
        if (e_6.altKey || e_6.ctrlKey || e_6.metaKey || e_6.shiftKey || e_6.defaultPrevented) {
          return;
        }
        const target = e_6.target as HTMLElement | null;
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable)) {
          return;
        }
        const i_0 = floors.findIndex(f_2 => f_2.floor === selected);
        const next = floors[i_0 + (e_6.key === "ArrowRight" ? 1 : -1)];
        if (next) {
          e_6.preventDefault();
          pick(next.floor);
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    };
    t13 = [floors, selected, pick];
    $[15] = floors;
    $[16] = selected;
    $[17] = t12;
    $[18] = t13;
  } else {
    t12 = $[17];
    t13 = $[18];
  }
  useEffect(t12, t13);
  const mapBox = useRef(null);
  useEffect(() => {
    const box = mapBox.current;
    if (!box || !selectedCoord) {
      return;
    }
    const node = box.querySelector(`g[data-coord="${selectedCoord[0]},${selectedCoord[1]}"]`);
    if (!node) {
      return;
    }
    const boxRect = box.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    box.scrollTo({
      top: box.scrollTop + (nodeRect.top - boxRect.top) - box.clientHeight / 2 + nodeRect.height / 2,
      left: box.scrollLeft + (nodeRect.left - boxRect.left) - box.clientWidth / 2 + nodeRect.width / 2,
      behavior: "smooth"
    });
  }, [selectedCoord]);
  const characterNames = useCharacterNames();
  const cat = {
    cards,
    relics,
    potions,
    events,
    monsters,
    encounters,
    cardScores,
    relicScores,
    gt
  };
  const header = model?.header;
  const characterId = cleanId(run.players?.[run.player_index ?? 0]?.character ?? header?.character ?? "");
  const character = characterId ? gt(`characters.${characterId}.title`) ?? characterName(characterId, characterNames) : "";
  const maxHp = model?.end?.maxHp ?? header?.startingMaxHp;
  const energyIcon = ENERGY_ICONS.has(characterId.toLowerCase()) ? characterId.toLowerCase() : "colorless";
  let t14;
  if ($[19] !== run.was_abandoned || $[20] !== run.win || $[21] !== t) {
    t14 = run.win ? t("Victory") : run.was_abandoned ? t("Abandoned") : t("Defeat");
    $[19] = run.was_abandoned;
    $[20] = run.win;
    $[21] = t;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const result = t14;
  let t15;
  if ($[23] !== run.username || $[24] !== t) {
    t15 = run.username?.trim() || t("Anonymous");
    $[23] = run.username;
    $[24] = t;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const who = t15;
  const map = model?.maps[act];
  const t16 = "mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8";
  const t17 = `${lp}/runs/${hash}`;
  let t18;
  if ($[26] !== t) {
    t18 = t("Back to run");
    $[26] = t;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t17 || $[29] !== t18) {
    t19 = <Link href={t17} className="text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]">← {t18}</Link>;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t) {
    t20 = t("Use \u2190 \u2192 to step floors");
    $[31] = t;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t20) {
    t21 = <span className="text-xs text-[var(--text-muted)]">{t20}</span>;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t19 || $[36] !== t21) {
    t22 = <div className="mb-4 flex flex-wrap items-center justify-between gap-2">{t19}{t21}</div>;
    $[35] = t19;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const t23 = characterId && <img src={imageUrl(`/static/images/characters/character_icon_${characterId.toLowerCase()}.webp`)} alt="" className="h-10 w-10" />;
  let t24;
  if ($[38] !== run.username) {
    t24 = <OwnerTheme username={run.username} />;
    $[38] = run.username;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  const t25 = `A${run.ascension ?? 0}`;
  let t26;
  if ($[40] !== character || $[41] !== t25 || $[42] !== who) {
    t26 = [who, character, t25].filter(Boolean);
    $[40] = character;
    $[41] = t25;
    $[42] = who;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  const t27 = t26.join(" \xB7 ");
  let t28;
  if ($[44] !== run.username) {
    t28 = <PlayerBadge username={run.username} className="ml-2" />;
    $[44] = run.username;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== t27 || $[47] !== t28) {
    t29 = <h1 className="text-xl font-bold text-[var(--text-primary)]">{t27}{t28}</h1>;
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  const t30 = run.win ? "text-[var(--accent-gold)]" : "text-[var(--accent-red)]";
  let t31;
  if ($[49] !== result || $[50] !== t30) {
    t31 = <span className={t30}>{result}</span>;
    $[49] = result;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  const t32 = floors.length;
  let t33;
  if ($[52] !== t) {
    t33 = t("floors");
    $[52] = t;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== run.run_time) {
    t34 = run.run_time ? ` · ${formatTime(run.run_time)}` : "";
    $[54] = run.run_time;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  const t35 = header?.buildId ? ` · ${header.buildId}` : "";
  let t36;
  if ($[56] !== model || $[57] !== t) {
    t36 = model && model.reloads > 0 ? ` · ${model.reloads} ${t("reloads")}` : "";
    $[56] = model;
    $[57] = t;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  let t37;
  if ($[59] !== floors || $[60] !== model || $[61] !== t) {
    t37 = model ? ` · ${floors.reduce(_temp13, 0)} ${t("decisions")}` : "";
    $[59] = floors;
    $[60] = model;
    $[61] = t;
    $[62] = t37;
  } else {
    t37 = $[62];
  }
  let t38;
  if ($[63] !== floors.length || $[64] !== t31 || $[65] !== t33 || $[66] !== t34 || $[67] !== t35 || $[68] !== t36 || $[69] !== t37) {
    t38 = <p className="text-sm text-[var(--text-muted)]">{t31}{" \xB7 "}{t32} {t33}{t34}{t35}{t36}{t37}</p>;
    $[63] = floors.length;
    $[64] = t31;
    $[65] = t33;
    $[66] = t34;
    $[67] = t35;
    $[68] = t36;
    $[69] = t37;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t24 || $[72] !== t29 || $[73] !== t38) {
    t39 = <div className="min-w-0">{t24}{t29}{t38}</div>;
    $[71] = t24;
    $[72] = t29;
    $[73] = t38;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== t23 || $[76] !== t39) {
    t40 = <header className="mb-5 flex flex-wrap items-center gap-4">{t23}{t39}</header>;
    $[75] = t23;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== model || $[79] !== t) {
    t41 = model && captureIsComplete(model) === false && <p className="mb-4 rounded-lg border border-[var(--accent-red)] px-3 py-2 text-sm text-[var(--text-secondary)]">{model.end?.captureStatus === "truncated" ? t("The recording stopped before the run ended, so the later floors are missing.") : t("The recording dropped some of this run, so parts of it are missing.")}{model.end?.lostCount !== undefined && ` ${t("Lines not captured: {n}", {
        n: model.end.lostCount
      })}`}</p>;
    $[78] = model;
    $[79] = t;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== model || $[82] !== t) {
    t42 = model && model.gaps.length > 0 && <p className="mb-4 text-xs text-[var(--text-muted)]">{t("Missing from the record: {n} lines in {places} places", {
        n: model.gaps.reduce(_temp14, 0),
        places: model.gaps.length
      })}{model.gaps.some(_temp15) && ` · ${t("Floors affected: {list}", {
        list: [...new Set(model.gaps.flatMap(_temp16))].join(", ")
      })}`}</p>;
    $[81] = model;
    $[82] = t;
    $[83] = t42;
  } else {
    t42 = $[83];
  }
  let t43;
  if ($[84] !== model || $[85] !== t) {
    t43 = model && model.malformedLines > 0 && <p className="mb-4 text-xs text-[var(--text-muted)]">{t("Lines that could not be read: {n}", {
        n: model.malformedLines
      })}</p>;
    $[84] = model;
    $[85] = t;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== floors || $[88] !== maxHp || $[89] !== model || $[90] !== selected) {
    t44 = model && <RunCharts model={model} floors={floors} maxHp={maxHp} selected={selected ?? -1} onPick={pick} />;
    $[87] = floors;
    $[88] = maxHp;
    $[89] = model;
    $[90] = selected;
    $[91] = t44;
  } else {
    t44 = $[91];
  }
  let t45;
  if ($[92] !== error || $[93] !== t) {
    t45 = error && <p className="text-sm text-[var(--accent-red)]">{t("Couldn't load the replay.")} {error}</p>;
    $[92] = error;
    $[93] = t;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] !== error || $[96] !== model || $[97] !== t) {
    t46 = !model && !error && <p className="text-sm text-[var(--text-muted)]">{t("Loading replay\u2026")}</p>;
    $[95] = error;
    $[96] = model;
    $[97] = t;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  const t47 = model && <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr]"><aside className="space-y-4">{acts.length > 1 && <div className="flex gap-1.5">{acts.map(a_1 => <button key={a_1} type="button" onClick={() => {
          const first_0 = floors.find(f_4 => f_4.act === a_1);
          if (first_0) {
            pick(first_0.floor);
          }
        }} className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${a_1 === act ? "border-[var(--accent-gold)] text-[var(--accent-gold)]" : "border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}>{t("Act")} {a_1}{model.actNames[a_1] ? ` · ${model.actNames[a_1].replace(/_/g, " ").toLowerCase()}` : ""}</button>)}</div>}{map && map.nodes.length > 0 && <div ref={mapBox} className="max-h-[70vh] overflow-auto rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-2"><LiveMap map={map} path={path_0} pathEdges={pathEdges} selected={selectedCoord} monsters={monsters} encounters={encounters} actName={model.actNames[act]} character={characterId} route={{
          boss: map.boss ? {
            id: map.boss
          } : undefined,
          ancient: map.ancient ? {
            id: map.ancient
          } : undefined
        }} onSelect={c_0 => {
          const floor_0 = coordToFloor.get(`${c_0[0]},${c_0[1]}`);
          if (floor_0 !== undefined) {
            pick(floor_0);
          }
        }} />{!positionsRecorded ? <p className="px-1 pt-2 text-[11px] text-[var(--text-muted)]">{t("This recording did not include map positions.")}</p> : unplaced > 0 ? <p className="px-1 pt-2 text-[11px] text-[var(--text-muted)]">{t("Floors with no recorded position: {n}", {
            n: unplaced
          })}</p> : null}{offMap > 0 && <p className="px-1 pt-1 text-[11px] text-[var(--text-muted)]">{t("Recorded positions not on the map: {n}", {
            n: offMap
          })}</p>}</div>}<ol className="max-h-[60vh] overflow-y-auto rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] text-sm">{floors.filter(f_5 => f_5.act === act).map(f_6 => {
          const on = f_6.floor === selected;
          return <li key={f_6.floor}><button type="button" onClick={() => pick(f_6.floor)} className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors ${on ? "bg-[color-mix(in_srgb,var(--accent-gold)_14%,transparent)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-[var(--bg-primary)]"}`}><span className="w-6 text-right text-xs tabular-nums text-[var(--text-muted)]">{f_6.floor}</span><span className="w-5 text-center text-xs" aria-hidden={true}>{KIND_GLYPH[f_6.kind] ?? "\xB7"}</span><span className="min-w-0 flex-1 truncate">{floorTitle(f_6, cat, t)}</span><span className="text-[10px] text-[var(--text-muted)]">{t(KIND_LABEL[f_6.kind] ?? f_6.kind)}</span>{f_6.hpAfter !== undefined && <span className="w-8 text-right text-[10px] tabular-nums text-[var(--text-muted)]">{f_6.hpAfter}</span>}</button></li>;
        })}</ol></aside><main className="min-w-0 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4">{current ? <FloorPanel f={current} prev={floors[floors.indexOf(current) - 1]} cat={cat} maxHp={maxHp} who={who} energyIcon={energyIcon} playerId={characterId.toUpperCase()} recordingEndsHere={current === floors[floors.length - 1] && (!model?.end || model.end.terminalReason === "interrupted")} /> : <p className="text-sm text-[var(--text-muted)]">{t("Pick a floor.")}</p>}</main></div>;
  let t48;
  if ($[99] !== t22 || $[100] !== t40 || $[101] !== t41 || $[102] !== t42 || $[103] !== t43 || $[104] !== t44 || $[105] !== t45 || $[106] !== t46 || $[107] !== t47) {
    t48 = <div className={t16}>{t22}{t40}{t41}{t42}{t43}{t44}{t45}{t46}{t47}</div>;
    $[99] = t22;
    $[100] = t40;
    $[101] = t41;
    $[102] = t42;
    $[103] = t43;
    $[104] = t44;
    $[105] = t45;
    $[106] = t46;
    $[107] = t47;
    $[108] = t48;
  } else {
    t48 = $[108];
  }
  return t48;
}
function _temp16(g_0) {
  return g_0.floor === undefined ? [] : [g_0.floor];
}
function _temp15(g_1) {
  return g_1.floor !== undefined;
}
function _temp14(a_0, g) {
  return a_0 + g.count;
}
function _temp13(n, f_3) {
  return n + f_3.decisions.length;
}
function _temp12(e_5) {
  return e_5.offMap;
}
function _temp11(e_4) {
  return !e_4.coord;
}
function _temp10(e_1) {
  return e_1.coord ? [e_1.coord] : [];
}
function _temp9(a, b) {
  return a - b;
}
function _temp8(f_1) {
  return f_1.act;
}
function _temp7() {
  return {} as Record<string, T>;
}
function _temp6(items) {
  const out = {};
  for (const x of Array.isArray(items) ? items : []) {
    out[x.id] = x;
  }
  return out;
}
async function _temp5(res) {
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.text();
}